import { useCallback, useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../../../store/projectStore';
import { getFloorPlanReferenceImages } from '../../../utils/floorPlanGeometry';

export type ReferenceImageCorner = 'nw' | 'ne' | 'sw' | 'se';

export type LiveReferenceGeometry = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

type Gesture =
  | {
      kind: 'move';
      id: string;
      startMouseX: number;
      startMouseY: number;
      originX: number;
      originY: number;
      width: number;
      height: number;
    }
  | {
      kind: 'resize';
      id: string;
      corner: ReferenceImageCorner;
      originX: number;
      originY: number;
      originW: number;
      originH: number;
      aspect: number;
    };

const MIN_SIZE = 48;

function aspectLockedResize(
  corner: ReferenceImageCorner,
  originX: number,
  originY: number,
  originW: number,
  originH: number,
  aspect: number,
  mouseX: number,
  mouseY: number
): { x: number; y: number; width: number; height: number } {
  const anchorX = corner.includes('w') ? originX + originW : originX;
  const anchorY = corner.includes('n') ? originY + originH : originY;

  const rawW = Math.abs(mouseX - anchorX);
  const rawH = Math.abs(mouseY - anchorY);
  const scaleW = rawW / Math.max(originW, 1);
  const scaleH = rawH / Math.max(originH, 1);
  let scale = Math.max(scaleW, scaleH);
  const minScale = MIN_SIZE / Math.min(originW, originH);
  scale = Math.max(scale, minScale);

  const width = originW * scale;
  const height = width / (aspect || originW / Math.max(originH, 1));

  const x = corner.includes('w') ? anchorX - width : anchorX;
  const y = corner.includes('n') ? anchorY - height : anchorY;
  return { x, y, width, height };
}

/**
 * Once an image is selected (double-click), drag the body to move and
 * corner handles for aspect-locked scale. Commits once on pointerup.
 */
export function useReferenceImageTransform(opts: {
  enabled: boolean;
  getCanvasCoordinates: (clientX: number, clientY: number) => { x: number; y: number };
}) {
  const { enabled, getCanvasCoordinates } = opts;
  const [liveGeometry, setLiveGeometry] = useState<LiveReferenceGeometry | null>(null);
  const liveRef = useRef<LiveReferenceGeometry | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const justFinishedRef = useRef(false);
  const getCoordsRef = useRef(getCanvasCoordinates);
  getCoordsRef.current = getCanvasCoordinates;

  const setLive = useCallback((next: LiveReferenceGeometry | null) => {
    liveRef.current = next;
    setLiveGeometry(next);
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const g = gestureRef.current;
      if (!g) return;
      const pos = getCoordsRef.current(e.clientX, e.clientY);

      if (g.kind === 'move') {
        setLive({
          id: g.id,
          x: g.originX + (pos.x - g.startMouseX),
          y: g.originY + (pos.y - g.startMouseY),
          width: g.width,
          height: g.height,
        });
        return;
      }

      const next = aspectLockedResize(
        g.corner,
        g.originX,
        g.originY,
        g.originW,
        g.originH,
        g.aspect,
        pos.x,
        pos.y
      );
      setLive({ id: g.id, ...next });
    };

    const onUp = () => {
      const g = gestureRef.current;
      const live = liveRef.current;
      gestureRef.current = null;
      if (live && g && live.id === g.id) {
        useProjectStore.getState().updateFloorPlanReferenceImage(
          live.id,
          { x: live.x, y: live.y, width: live.width, height: live.height },
          true
        );
      }
      setLive(null);
      justFinishedRef.current = true;
      window.setTimeout(() => {
        justFinishedRef.current = false;
      }, 120);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [setLive]);

  const resolveImage = useCallback((id: string) => {
    const fp = useProjectStore.getState().getFloorPlan();
    if (!fp) return null;
    return getFloorPlanReferenceImages(fp).find((img) => img.id === id) ?? null;
  }, []);

  const beginMove = useCallback(
    (id: string, e: React.PointerEvent) => {
      if (!enabled) return;
      if (useProjectStore.getState().selectedReferenceImageId !== id) return;
      e.preventDefault();
      e.stopPropagation();
      const img = resolveImage(id);
      if (!img) return;
      const pos = getCoordsRef.current(e.clientX, e.clientY);
      const base = liveRef.current?.id === id ? liveRef.current : img;
      gestureRef.current = {
        kind: 'move',
        id,
        startMouseX: pos.x,
        startMouseY: pos.y,
        originX: base.x,
        originY: base.y,
        width: base.width,
        height: base.height,
      };
      setLive({
        id,
        x: base.x,
        y: base.y,
        width: base.width,
        height: base.height,
      });
    },
    [enabled, resolveImage, setLive]
  );

  const beginResize = useCallback(
    (id: string, corner: ReferenceImageCorner, e: React.PointerEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      const img = resolveImage(id);
      if (!img) return;
      const base = liveRef.current?.id === id ? liveRef.current : img;
      gestureRef.current = {
        kind: 'resize',
        id,
        corner,
        originX: base.x,
        originY: base.y,
        originW: base.width,
        originH: base.height,
        aspect: base.width / Math.max(base.height, 1),
      };
      setLive({
        id,
        x: base.x,
        y: base.y,
        width: base.width,
        height: base.height,
      });
    },
    [enabled, resolveImage, setLive]
  );

  return {
    liveGeometry,
    beginMove,
    beginResize,
    justFinishedTransform: () => justFinishedRef.current,
    isTransforming: liveGeometry != null,
  };
}
